import { useState } from 'react'
import { ArchModule } from 'apis/entities/architecture.entity'
import Svg, { Icon, IconClass } from 'components/Svg'
import Spinner from './Spinner'
import CheckboxDropdown from './CheckboxDropdown'
import { ToastUtil } from 'utils/ToastUtil'
import ButtonSpinner from './ButtonSpinner'

interface UpdateTopicLinksModalProps {
  modules: ArchModule[]
  isFetchingCourse: boolean
  moduleLabel: string
  onSave: (editedLinks: Record<string, string>) => Promise<void>
  onClose: () => void
}

const COL_NUM = { width: 16, minWidth: 16 }
const COL_TITLE = { width: 278, minWidth: 278 }

const truncate = (s: string, max = 82) =>
  s.length > max ? s.slice(0, max) + '...' : s

export default function UpdateTopicLinksModal({
  modules,
  isFetchingCourse,
  moduleLabel,
  onSave,
  onClose,
}: UpdateTopicLinksModalProps) {
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(
    new Set(),
  )
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const moduleDropdownItems = modules.map((m, i) => ({
    id: m.id,
    name: truncate(`${moduleLabel} ${i + 1}: ${m.name}`),
  }))
  const [editedLinks, setEditedLinks] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      modules.flatMap((m) => m.topics.map((t) => [t.id, t.link ?? ''])),
    ),
  )

  const isModified = (
    topicId: string,
    originalLink: string | null | undefined,
  ) => editedLinks[topicId] !== (originalLink ?? '')

  const modifiedTopicCount = modules.reduce(
    (acc, m) => acc + m.topics.filter((t) => isModified(t.id, t.link)).length,
    0,
  )

  const handleClose = () => {
    if (
      modifiedTopicCount > 0 &&
      !window.confirm(
        'You have unsaved changes. Are you sure you want to close?',
      )
    )
      return
    onClose()
  }

  const toggleModule = (id: string) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const visibleModules =
    selectedModuleIds.length === 0
      ? modules
      : modules.filter((m) => selectedModuleIds.includes(m.id))

  const handleSave = async () => {
    if (modifiedTopicCount === 0) {
      ToastUtil.warning('No links have been changed')
      return
    }
    try {
      setIsSaving(true)
      await onSave(editedLinks)
      ToastUtil.success(
        `${modifiedTopicCount} topic link${
          modifiedTopicCount === 1 ? '' : 's'
        } updated`,
      )
    } catch {
      ToastUtil.error('Failed to update links. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="absolute z-[1000] flex h-full w-full items-center justify-center bg-[#D5D5DBAA]">
      <div className="flex max-h-[85%] w-[75%] flex-col rounded-[3px] bg-white px-[24px] pb-[24px] pt-[12px] shadow-xl gap-[12px]">
        {/* Close */}
        <div className="flex justify-end">
          <div className="cursor-pointer" onClick={handleClose}>
            <Svg icon={Icon.Close} className={IconClass.FillBlack2} />
          </div>
        </div>

        {/* Title */}
        <div className="flex items-center gap-3">
          <Svg icon={Icon.Link} />
          <span className="font-clancy text-[20px] font-bold leading-[28px] text-[#050505]">
            Update topic page links
          </span>
        </div>

        {/* Subtitle + filter */}
        <div className="flex items-center gap-6">
          <p className="flex-1 font-clancy text-[14px] leading-[22px] text-[#050505]">
            Update the page URLs for all topics.
            <br />
            Topic titles cannot be edited.
          </p>
          <div className="w-[500px]">
            <CheckboxDropdown
              label={`All ${moduleLabel.toLowerCase()}s`}
              showAllLabel={`All ${moduleLabel.toLowerCase()}s`}
              items={moduleDropdownItems}
              onSelected={setSelectedModuleIds}
            />
          </div>
        </div>

        {/* Table */}
        {isFetchingCourse && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        )}

        {!isFetchingCourse && modules.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <p>No {moduleLabel.toLowerCase()}s found</p>
          </div>
        )}

        {!isFetchingCourse && modules.length > 0 && (
          <div className="mentem-scrollbar-2 flex-1 overflow-y-auto">
            {/* Column headers */}
            <div className="flex h-[36px] items-center gap-[12px] bg-[#FFDC00] px-[12px] outline outline-1 outline-[#F2F2F2]">
              <div style={COL_NUM} />
              <div style={COL_TITLE}>
                <span className="font-clancy text-[14px] text-[#181716]">
                  Title
                </span>
              </div>
              <span className="font-clancy text-[14px] text-[#181716]">
                Page URL
              </span>
            </div>

            {visibleModules.map((module) => {
              const originalIndex = modules.findIndex((m) => m.id === module.id)
              return (
                <div key={module.id}>
                  {/* Module header */}
                  <div className="arch-module-list-item flex items-center justify-between gap-[12px] px-[12px]">
                    <div className="flex items-center gap-[12px]">
                      <div style={COL_NUM} />
                      <span>
                        {moduleLabel}{' '}
                        {String(originalIndex + 1).padStart(2, '0')}:{' '}
                        {module.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleModule(module.id)}
                      aria-label={`${
                        collapsedModules.has(module.id) ? 'Expand' : 'Collapse'
                      } ${moduleLabel.toLowerCase()} ${originalIndex + 1}`}
                      className="flex h-[20px] w-[20px] items-center justify-center mr-[26px]"
                    >
                      <Svg
                        icon={
                          collapsedModules.has(module.id) ? Icon.Down : Icon.Up
                        }
                        className={IconClass.FillWhite}
                      />
                    </button>
                  </div>

                  {/* Topics */}
                  {!collapsedModules.has(module.id) &&
                    module.topics.map((topic, topicIndex) => (
                      <div
                        key={topic.id}
                        className={`flex h-[36px] items-center gap-[12px] px-[12px] outline outline-1 outline-[#F2F2F2] ${
                          isModified(topic.id, topic.link)
                            ? 'bg-[#FFFAD9]'
                            : 'bg-white'
                        }`}
                      >
                        <div
                          className="flex items-center justify-center"
                          style={COL_NUM}
                        >
                          <span className="font-roboto text-[12px] font-semibold text-black">
                            {originalIndex + 1}.{topicIndex + 1}
                          </span>
                        </div>
                        <div className="flex items-center" style={COL_TITLE}>
                          <span className="font-roboto text-[12px] font-semibold text-black">
                            {topic.name}
                          </span>
                        </div>
                        <div className="flex flex-1 items-center self-stretch gap-[60px]">
                          <div
                            className="flex flex-1 items-center justify-center p-[6px] my-[3px]"
                            style={{
                              outline: `0.3px solid ${
                                isModified(topic.id, topic.link)
                                  ? '#007882'
                                  : '#181716'
                              }`,
                              outlineOffset: '-0.3px',
                            }}
                          >
                            <input
                              type="text"
                              className="w-full bg-transparent font-roboto text-[10px] italic font-normal underline text-[#737373] border-none outline-none overflow-hidden text-ellipsis whitespace-nowrap"
                              value={editedLinks[topic.id] ?? ''}
                              onChange={(e) =>
                                setEditedLinks((prev) => ({
                                  ...prev,
                                  [topic.id]: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div
                            style={{ width: 20, minWidth: 20, marginRight: 42 }}
                          >
                            {editedLinks[topic.id] && (
                              <a
                                href={editedLinks[topic.id]}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Svg icon={Icon.ExternalLink} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Svg icon={Icon.Info} />
            <span className="font-clancy text-[14px] leading-[22px] text-[#050505]">
              You are updating {modifiedTopicCount} topic link
              {modifiedTopicCount === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="h-[36px] border border-black px-[16px] font-robotoMono text-[14px] font-medium text-[#181716] disabled:opacity-50"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="flex h-[36px] items-center gap-2 bg-[#FFDC00] px-[16px] font-robotoMono text-[14px] font-medium text-[#181716] disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving && <ButtonSpinner />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
